import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const processSteps = [
  { num: '01', jp: '専門性の発掘', en: 'Discovery', strategy: true },
  { num: '02', jp: '意味の言語化', en: 'Meaning Edit', strategy: true },
  { num: '03', jp: 'デザイン・制作', en: 'Design & Make', highlight: true },
  { num: '04', jp: '発信・展開', en: 'Publish' },
  { num: '05', jp: '共有・循環', en: 'Sharing' },
  { num: '06', jp: '信頼・市場', en: 'Market', accent: true },
];

const principles = [
  '山事的に説明するのではなく、心に届く言葉に翻訳してデザインする。',
  '発信するのではなく、相手に届く形につくる。',
  '納品で終わりではなく、組織に残る資産にする。',
];

export default function DefinitionSection() {
  const left = useScrollAnimation();
  const steps = useStaggerAnimation(processSteps.length);

  return (
    <section className="texture-definition py-28 md:py-40 bg-warm-light relative overflow-hidden">

      {/* ── 「プロセス・変換・接続」をテーマにした装飾 ── */}

      {/* 左側縦ライン（セクションラベル） */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3">
        <span className="block w-px h-10 bg-divider" />
        <span className="text-[10px] font-garamond italic text-muted-light tracking-widest-jp" style={{ writingMode: 'vertical-rl' }}>Our Approach</span>
        <span className="block w-px h-10 bg-divider" />
      </div>

      {/* 上部中央の「変換ゲート」— 入力→変換→出力の構造 */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none hidden lg:block">
        {/* 変換を示す二重線 */}
        <div className="absolute w-px h-16 bg-gradient-to-b from-accent-gold/30 to-transparent" style={{ left: -20, top: 0 }} />
        <div className="absolute w-px h-16 bg-gradient-to-b from-accent-amber/25 to-transparent" style={{ left: 0, top: 0 }} />
        <div className="absolute w-px h-16 bg-gradient-to-b from-accent-gold/20 to-transparent" style={{ left: 20, top: 0 }} />
        {/* 上端の接続点 */}
        <div className="absolute w-2 h-2 rounded-full bg-accent-gold/40" style={{ left: -21, top: -1 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-amber/35" style={{ left: -1, top: -1 }} />
        <div className="absolute w-2 h-2 rounded-full bg-accent-gold/30" style={{ left: 19, top: -1 }} />
      </div>

      {/* 右側の「フロー矢印群」— プロセスの流れ */}
      <div className="absolute right-8 top-1/4 pointer-events-none hidden lg:block">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="flex items-center gap-1 mb-6">
            <div className="w-8 h-px" style={{ background: `rgba(${i % 2 === 0 ? '212,122,46' : '201,168,76'},${0.25 - i * 0.03})` }} />
            <div
              className="w-0 h-0"
              style={{
                borderTop: '3px solid transparent',
                borderBottom: '3px solid transparent',
                borderLeft: `5px solid rgba(${i % 2 === 0 ? '212,122,46' : '201,168,76'},${0.25 - i * 0.03})`,
              }}
            />
          </div>
        ))}
      </div>

      {/* 右上の「変換を示す菱形チェーン」 */}
      <div className="absolute top-6 right-6 lg:top-12 lg:right-20 pointer-events-none">
        {/* 大菱形 */}
        <div className="absolute w-10 h-10 lg:w-18 lg:h-18 border-2 border-accent-gold/38 rotate-45" style={{ width: 40, height: 40 }} />
        {/* 接続線 */}
        <div className="absolute w-px h-6 bg-accent-gold/25" style={{ left: 20, top: 40 }} />
        {/* 中菱形 */}
        <div className="absolute border border-accent-amber/42 rotate-45" style={{ width: 20, height: 20, left: 10, top: 66 }} />
        {/* 接続線 */}
        <div className="absolute w-px h-4 bg-accent-amber/20" style={{ left: 20, top: 86 }} />
        {/* 小菱形 */}
        <div className="absolute bg-accent-gold/28 rotate-45" style={{ width: 8, height: 8, left: 16, top: 100 }} />
      </div>

      {/* 右下の「接続された同心円」— 変換後の広がり */}
      <div className="absolute -bottom-10 right-1/4 w-28 h-28 lg:w-52 lg:h-52 rounded-full border border-accent-amber/18 pointer-events-none" />
      <div className="absolute -bottom-16 right-[22%] w-44 h-44 lg:w-76 lg:h-76 rounded-full border border-accent-gold/12 pointer-events-none hidden sm:block" />
      {/* 中心点 */}
      <div className="absolute bottom-0 right-[25%] w-2 h-2 rounded-full bg-accent-amber/35 pointer-events-none hidden lg:block" />

      {/* 右側ドットグリッド（変換後の広がり） */}
      <div className="absolute top-1/4 right-2 lg:right-4 w-24 h-32 lg:w-44 lg:h-60 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.28) 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />
      </div>

      {/* 左下の「出力・完成」を示す装飾 */}
      <div className="absolute bottom-8 left-6 lg:bottom-16 lg:left-16 pointer-events-none">
        {/* 完成した菱形（全辺揃っている） */}
        <div className="w-6 h-6 lg:w-12 lg:h-12 border-2 border-accent-teal/28 rotate-45" />
        <div className="absolute w-3 h-3 lg:w-6 lg:h-6 border border-accent-teal/35 rotate-45" style={{ left: 6, top: 6 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-teal/40" style={{ left: 9, top: 9 }} />
      </div>

      {/* 左中央の「変換プロセス」を示す水平フロー */}
      <div className="absolute left-16 top-1/2 -translate-y-1/2 pointer-events-none hidden xl:block">
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: `rgba(${i === 0 ? '74,158,142' : i === 1 ? '212,122,46' : '201,168,76'},0.35)` }} />
              <div className="w-6 h-px" style={{ background: `rgba(${i === 0 ? '74,158,142' : i === 1 ? '212,122,46' : '201,168,76'},0.20)` }} />
              <div className="w-1 h-1 rounded-full" style={{ backgroundColor: `rgba(${i === 0 ? '74,158,142' : i === 1 ? '212,122,46' : '201,168,76'},0.25)` }} />
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
          {/* Left */}
          <div
            ref={left.ref as React.RefObject<HTMLDivElement>}
            className="md:col-span-5 transition-all duration-700"
            style={{ opacity: left.isVisible ? 1 : 0, transform: left.isVisible ? 'translateX(0)' : 'translateX(-32px)' }}
          >
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">What Makes Us Different</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight mb-6 text-3xl md:text-4xl">
              広告・制作で、<br />意味を社会に届ける。
            </h2>
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp mb-10">
              広告・グラフィック・Web・映像の制作を核に持つ広告制作会社です。<br />
              ただし、見た目だけを整えるのではなく、
              組織の専門性を「意味」として掘り起こし、
              届く広告・制作物に落とし込んでいきます。
            </p>
            <div className="space-y-0 border-t border-divider">
              {principles.map((p, i) => (
                <div
                  key={p}
                  className="flex items-start gap-5 py-6 border-b border-divider transition-all duration-500"
                  style={{
                    opacity: left.isVisible ? 1 : 0,
                    transform: left.isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transitionDelay: `${200 + i * 100}ms`,
                  }}
                >
                  <span className={`block w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2.5 ${i === 0 ? 'bg-accent-teal' : i === 1 ? 'bg-accent-amber' : 'bg-accent-gold'}`} />
                  <p className="font-serif-jp text-base md:text-lg font-light text-charcoal tracking-normal-jp leading-snug">{p}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div className="md:col-span-7 flex items-center">
            <div
              ref={steps.ref as React.RefObject<HTMLDivElement>}
              className="w-full"
            >
              <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-8">制作プロセス</p>
              <div className="relative">
                <div className="absolute left-5 top-5 bottom-5 w-px bg-divider hidden md:block" />
                <div className="space-y-0">
                  {/* 戦略フェーズ */}
                  <div className="mb-0 bg-accent-gold-light/60">
                    {processSteps.filter(s => s.strategy).map((step, i) => (
                      <div
                        key={step.num}
                        className="flex items-center gap-6 py-5 border-b border-accent-gold/20 relative transition-all duration-500"
                        style={{
                          opacity: steps.visibleCount > i ? 1 : 0,
                          transform: steps.visibleCount > i ? 'translateX(0)' : 'translateX(24px)',
                          transitionDelay: `${i * 80}ms`,
                        }}
                      >
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border z-10 border-accent-gold/40 bg-warm-white/80">
                          <span className="font-garamond text-xs text-accent-gold">{step.num}</span>
                        </div>
                        <div className="flex-1 flex items-baseline gap-4">
                          <span className="font-serif-jp text-lg font-light tracking-normal-jp text-charcoal">{step.jp}</span>
                          <span className="font-garamond text-xs italic text-muted-light">{step.en}</span>
                        </div>
                        {i === 0 && (
                          <span className="text-[10px] font-sans-jp text-accent-gold tracking-widest-jp border border-accent-gold/40 px-2 py-0.5 whitespace-nowrap ml-auto">
                            戦略フェーズ
                          </span>
                        )}
                        {i !== 0 && (
                          <span className="text-accent-gold/40 text-xs font-sans-jp ml-auto pr-2">↓</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* 制作フェーズ以降 */}
                  {processSteps.filter(s => !s.strategy).map((step) => {
                    const globalIndex = processSteps.findIndex(s => s.num === step.num);
                    return (
                      <div
                        key={step.num}
                        className={`flex items-center gap-6 py-5 border-b border-divider last:border-b-0 relative transition-all duration-500 ${step.highlight ? 'bg-accent-teal/5' : ''}`}
                        style={{
                          opacity: steps.visibleCount > globalIndex ? 1 : 0,
                          transform: steps.visibleCount > globalIndex ? 'translateX(0)' : 'translateX(24px)',
                          transitionDelay: `${globalIndex * 80}ms`,
                        }}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 border z-10 ${
                          step.accent ? 'border-charcoal bg-charcoal' :
                          step.highlight ? 'border-accent-teal bg-accent-teal' :
                          'border-divider bg-warm-light'
                        }`}>
                          <span className={`font-garamond text-xs ${
                            step.accent || step.highlight ? 'text-warm-white' : 'text-muted-light'
                          }`}>{step.num}</span>
                        </div>
                        <div className="flex-1 flex items-baseline gap-4">
                          <span className={`font-serif-jp text-lg font-light tracking-normal-jp ${
                            step.accent ? 'text-charcoal' :
                            step.highlight ? 'text-accent-teal' :
                            'text-charcoal'
                          }`}>{step.jp}</span>
                          <span className="font-garamond text-xs italic text-muted-light">{step.en}</span>
                        </div>
                        {step.highlight && (
                          <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp border border-accent-teal px-2 py-0.5 whitespace-nowrap ml-auto">
                            制作フェーズ
                          </span>
                        )}
                        {!step.highlight && !step.accent && (
                          <span className="text-divider text-xs font-sans-jp ml-auto pr-2">↓</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="mt-8 font-sans-jp text-xs text-muted-light leading-relaxed-jp">
                ※ 戦略フェーズのみ、制作フェーズのみのご依頼も承ります。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
